import ConfigurationService from './ConfigurationService';
import { initializeTracing } from '../tracing';
import { initializeSentry } from './sentryInitializer';
import { PERFORMANCE_DEFAULTS, PERFORMANCE_ENV_KEYS, SERVICE_INIT_EVENTS } from '../constants/performanceConstants';
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * Service Configuration and Initialization Orchestrator
 * 
 * Manages the initialization of frontend services in two phases:
 * 1. Critical Phase: Must complete before UI interactions (e.g., Configuration)
 * 2. Deferred Phase: Can run in background (e.g., Sentry, Tracing, Analytics)
 */

interface InitResult {
    success: boolean;
    error?: Error;
}

/**
 * Initializes critical services required for the application to render correctly.
 * For the fastest startup, ConfigurationService uses localStorage caching to return immediately.
 * 
 * @returns Promise that resolves when critical services are ready
 */
export async function initializeCriticalServices(): Promise<InitResult> {
    try {
        const start = performance.now();

        // ConfigurationService is the only strictly critical service
        await ConfigurationService.initialize();

        const duration = performance.now() - start;
        console.debug(`⚡ Critical services initialized in ${duration.toFixed(2)}ms`); // Changed from logger to console.debug to avoid dependency cycle if logger uses config

        return { success: true };
    } catch (error) {
        console.error('Failed to initialize critical services', error);
        return {
            success: false,
            error: error instanceof Error ? error : new Error(String(error))
        };
    }
}

/**
 * Initializes non-critical services in the background.
 * Uses requestIdleCallback or setTimeout to avoid blocking main thread interactions.
 */
export function initializeDeferredServices(): void {
    const initLogic = async () => {
        try {
            const delay = getDeferredDelay();
            if (delay > 0) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            console.groupCollapsed('🚀 Initializing deferred services');

            // Initialize all deferred services in parallel
            const results = await Promise.allSettled([
                initializeTracing(),
                initializeSentry(),
                initializeAxeCore()
            ]);

            // Report results
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.warn(`Service ${index} failed to initialize:`, result.reason);
                }
            });

            console.log('✅ All deferred services started');
            console.groupEnd();

            // Dispatch event for any listeners waiting for full readiness
            window.dispatchEvent(new CustomEvent(SERVICE_INIT_EVENTS.ALL_SERVICES_READY));

        } catch (error) {
            console.error('Deferred initialization error', error);
        }
    };

    // Schedule execution
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(initLogic, { timeout: 5000 });
    } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(initLogic, 100);
    }
}

/**
 * Initializes axe-core for accessibility testing in development only.
 */
async function initializeAxeCore(): Promise<void> {
    if (import.meta.env.DEV) {
        try {
            // Dynamic import to avoid including in production bundle
            const axe = await import('@axe-core/react');
            await axe.default(React, ReactDOM, 1000);
            console.debug('♿ axe-core initialized');
        } catch (error) {
            console.warn('Failed to load axe-core', error);
        }
    }
}

/**
 * Helper to get configured delay for deferred services
 */
function getDeferredDelay(): number {
    const key = PERFORMANCE_ENV_KEYS.DEFERRED_INIT_DELAY_MS;
    if (window.__RUNTIME_CONFIG__?.[key]) {
        return Number(window.__RUNTIME_CONFIG__[key]);
    }
    return PERFORMANCE_DEFAULTS.DEFERRED_INIT_DELAY_MS;
}
